import { dynamicPageDeletePopupAction } from '@wix/bi-logger-wix-code-data/v2'
import { deleteDynamicPagePanel as deletePanelBiData } from '@wix/wix-data-client-common/dist/esm/bi/panelDataForBi'
import openErrorPanel from '@wix/wix-data-client-common/dist/esm/panels/error-panel/openErrorPanel'
import { getIsCollectionDeleted } from '../utils/isCollectionDeleted'
import { getBiDefaults } from './getBiDefaults'
import { isDPCollectionDeleted } from './isDPCollectionDeleted'
import {
  resolveHasDpVariants,
  isEditor3OrStudio2,
} from '@wix/wix-data-client-common'
import type {
  EditorOriginToBI,
  AppApi,
  EDITOR_TYPE,
} from '@wix/wix-data-client-common'
import type {
  EditorSDK,
  I18n,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type { PageRef } from '@wix/editor-platform-sdk-types'

type PanelLabelsParams = {
  t: I18n['t']
  collectionIsDeleted: boolean
  pageTitle: string
  editorType: EDITOR_TYPE
  hasDpVariants: boolean
}

const getPanelLabels = ({
  t,
  collectionIsDeleted,
  pageTitle,
  editorType,
  hasDpVariants,
}: PanelLabelsParams) => {
  if (isEditor3OrStudio2(editorType)) {
    return {
      headerText: t('Editor3_Delete_Dynamic_Page_Panel_Title'),
      topDescriptionText: t('Editor3_Delete_Dynamic_Page_Panel_Description', {
        pageName: pageTitle,
      }),
      mainActionText: t(
        'Editor3_Delete_Dynamic_Page_Panel_Primary_Button_Text',
      ),
      secondaryActionText: t(
        'Editor3_Delete_Dynamic_Page_Panel_Secondary_Button_Text',
      ),
    }
  }

  const withDpVariantsDeleteButton = t(
    'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_With_Variants_Button2',
  )

  return collectionIsDeleted
    ? {
        headerText: t(
          'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Removed_Collection_Title',
        ),
        topDescriptionText: hasDpVariants
          ? t(
              'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Removed_Collection_With_Variants_Text',
              { nameOfPage: pageTitle },
            )
          : t(
              'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Removed_Collection_Text',
              { nameOfPage: pageTitle },
            ),
        mainActionText: hasDpVariants
          ? withDpVariantsDeleteButton
          : t(
              'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Removed_Collection_Button2',
            ),
        secondaryActionText: t(
          'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Removed_Collection_Button1',
        ),
      }
    : {
        headerText: t(
          'Workspace_DYNAMIC_PAGES_WARNING_DELETE_DYNAMIC_PAGE_TITLE',
        ),
        topDescriptionText: hasDpVariants
          ? t(
              'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_With_Variants_Text',
              { nameOfPage: pageTitle },
            )
          : t('Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Text', {
              nameOfPage: pageTitle,
            }),
        mainActionText: hasDpVariants
          ? withDpVariantsDeleteButton
          : t('Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Button2'),
        secondaryActionText: t(
          'Workspace_Dynamic_Pages_Warning_Delete_Dynamic_Page_Button1',
        ),
      }
}

type OpenDeletePagePanelDependencies = {
  editorSdkProxy: EditorSDK
  logger: Logger
  appApi: AppApi
  collectionsApi: CollectionsApi
  i18n: I18n
  editorType: EDITOR_TYPE
}

export const openDeletePagePanel = async (
  {
    editorSdkProxy,
    logger,
    appApi,
    collectionsApi,
    i18n,
    editorType,
  }: OpenDeletePagePanelDependencies,
  {
    pageRef,
    origin,
  }: {
    pageRef: PageRef
    origin: EditorOriginToBI
  },
) => {
  const [routerRef, hasDpVariants] = await Promise.all([
    editorSdkProxy.document.routers.getByPage({ pageRef }),
    resolveHasDpVariants({ pageRef, editorSdkProxy }),
  ])

  if (!routerRef) {
    await editorSdkProxy.pages.remove({ pageRef })
    return
  }

  const t = i18n.t.bind(i18n)

  const isCollectionDeleted = getIsCollectionDeleted(collectionsApi)
  const collectionIsDeleted = await isDPCollectionDeleted({
    editorSdkProxy,
    routerRef,
    pageRef,
    isCollectionDeleted,
  })

  const labels = getPanelLabels({
    t,
    collectionIsDeleted,
    pageTitle: (await editorSdkProxy.pages.getPageData({ pageRef })).title,
    editorType,
    hasDpVariants,
  })

  const biDefaults = await getBiDefaults({
    editorSdkProxy,
    collectionsApi,
    pageRef,
    routerRef,
    origin,
    panelName: deletePanelBiData.panelName,
  })

  await openErrorPanel({
    ...labels,
    editorSdkProxy,
    logger,
    helpId: '35b6fd54-22ae-4251-aa8d-2db717085ea3',
    onMainActionClick: async () => {
      const pageExists = !!(await editorSdkProxy.pages.getPageData({
        pageRef,
      }))
      if (!pageExists) {
        return
      }

      logger.bi(
        dynamicPageDeletePopupAction({
          ...biDefaults,
          category: 'delete',
        }),
      )
      await appApi.deleteDynamicPage({ pageRef })
    },
    onSecondaryActionClick: () => {
      logger.bi(
        dynamicPageDeletePopupAction({
          ...biDefaults,
          category: 'cancel',
        }),
      )
    },
    onCloseClick: () => {
      logger.bi(
        dynamicPageDeletePopupAction({
          ...biDefaults,
          category: 'close',
        }),
      )
    },
    illustration: undefined,
    bottomDescriptionText: undefined,
  })
}
